import { ButtonProps, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Category } from '@/themes/variant-interfaces/Category';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, IconButton, Stack, SvgIconProps, SxProps, useTheme } from '@mui/material';
import { cloneElement, Fragment, isValidElement, ReactNode } from 'react';
import { MaterialSymbolProps } from 'react-material-symbols';

export type BannerStatus = 'error' | 'info' | 'success' | 'warning';
interface BannerProps {
  actionButtons?: ReactNode[],
  icon?: ReactNode,
  messages?: string[],
  onClose?: () => void,
  showMobileVersion?: boolean,
  stackMessages?: boolean,
  status: BannerStatus,
  sx?: SxProps,
  title: string,
  titleTextCategory?: Category,
  variant?: 'filled' | 'outlined' | 'standard',
}

export const Banner = ({
  actionButtons,
  icon,
  messages,
  onClose,
  showMobileVersion,
  stackMessages = false,
  status,
  sx,
  title,
  titleTextCategory = 'label-large',
  variant = 'filled',
}: BannerProps) => {
  const theme = useTheme();
  const { isMobile } = useMediaQuery();

  const shouldShowMobileVersion = showMobileVersion ?? isMobile;

  const coloredIcon = icon && isValidElement(icon)
    ? cloneElement(icon as React.ReactElement<SvgIconProps | MaterialSymbolProps>, {
      ...('sx' in icon.props ? {
        sx: {
          color: theme.palette[status].main,
          height: '20px',
          width: '20px',
        },
      } : {
        color: theme.palette[status].main,
        style: {
          height: '20px',
          width: '20px',
        },
      }),
    })
    : false;

  const ActionButtons = () => (
    <Stack direction="row">
      {actionButtons?.map((actionButton, i) => (
        <Fragment key={i}>
          {styleActionButtons(actionButton)}
          {i + 1 < actionButtons.length && (
            <HSpacer size="3" />
          )}
        </Fragment>
      ))}
    </Stack>
  );

  const styleActionButtons = (button: ReactNode) => {
    if (isValidElement<ButtonProps>(button)) {
      return cloneElement(button, {
        color: 'inherit',
        size: 'small',
        square: true,
        sx: { borderColor: 'rgba(0, 0, 0, 0.16)' },
        variant: 'outlined',
        ...button.props,
      });
    }
    return null;
  };

  return (
    <Alert
      icon={coloredIcon}
      severity={status}
      sx={{
        alignItems: shouldShowMobileVersion ? 'start' : 'center',
        bgcolor: theme.palette.alert[status].background,
        borderRadius: '8px',
        color: theme.palette.text.primary,
        padding: '4px 12px 4px 16px',
        '& .MuiAlert-message': { width: '100%' },
        ...sx,
      }}
      variant={variant}
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Stack>
          <Text category={titleTextCategory} sx={{ marginBottom: 0, marginTop: 'auto' }}>
            {title}
          </Text>
          {!!messages?.length && (
            <VSpacer size="2"/>
          )}
          {!!messages?.length && (
            (shouldShowMobileVersion || stackMessages)
              ? messages.map(message => (
                <Text category="body-medium"
                      key={message}
                      sx={{ color: theme.palette.text.primary }}
                >
                  {message}
                </Text>
              )) : (
                <Text category="body-medium">
                  {messages.join(' ')}
                </Text>
              )
          )}
          {shouldShowMobileVersion && !!actionButtons?.length && (
            <>
              <VSpacer size="3" />
              <ActionButtons />
            </>
          )}
        </Stack>
        {(!!onClose || !!actionButtons?.length) && (
          <Stack
            alignItems={shouldShowMobileVersion ? 'flex-start' : 'center'}
            alignSelf={shouldShowMobileVersion ? 'flex-start' : 'center'}
            direction="row"
          >
            {!shouldShowMobileVersion && !!actionButtons?.length && (
              <>
                <HSpacer size="4" />
                <ActionButtons />
              </>
            )}
            {!!onClose && (
              <>
                <HSpacer size="4" />
                <IconButton
                  onClick={onClose}
                  sx={{ height: 'fit-content', p: 0, width: 'fit-content' }}
                >
                  <CloseIcon />
                </IconButton>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Alert>
  );
};
